import { useMemo } from "react";
import { useTheme } from "@/contexts/ThemeContext";

// One Stripe Elements appearance for every payment and tax-address form, built from the
// site's own CSS variables so labels and fields are readable in light AND dark mode.
// Stripe needs real colour values (not var()), so they are read at render time.
const cssVar = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

export const buildStripeAppearance = (theme) => {
  const dark = theme === "dark";
  const text = cssVar("--text-primary", dark ? "#e7eef3" : "#0f172a");
  const muted = cssVar("--text-secondary", dark ? "#9ca3af" : "#6b7280");
  const surface = cssVar("--bg-surface", dark ? "#101826" : "#ffffff");
  const field = cssVar("--bg-subtle", dark ? "#16202e" : "#d8e1e5");
  const border = cssVar("--border-color", dark ? "#24303d" : "#b9c9d2");
  const accent = cssVar("--accent-primary", "#2575fc");
  return {
    theme: dark ? "night" : "stripe",
    labels: "above",
    variables: {
      colorPrimary: accent, colorBackground: surface, colorText: text,
      colorTextSecondary: muted, colorTextPlaceholder: muted, colorDanger: "#ef4444",
      colorIcon: muted, borderRadius: "10px",
    },
    rules: {
      ".Label": { color: text, fontWeight: "500" },
      ".Input": { backgroundColor: dark ? field : surface, color: text, border: `1px solid ${border}`, boxShadow: "none" },
      ".Input:focus": { border: `1px solid ${accent}`, boxShadow: `0 0 0 2px ${accent}33` },
      ".Input::placeholder": { color: muted },
      ".Tab": { backgroundColor: dark ? field : surface, color: text, border: `1px solid ${border}` },
      ".Tab--selected": { borderColor: accent, color: text },
      ".Block": { backgroundColor: dark ? field : surface, borderColor: border },
      ".Error": { color: "#ef4444" },
    },
  };
};

export const useStripeAppearance = () => {
  const { theme } = useTheme();
  return useMemo(() => buildStripeAppearance(theme), [theme]);
};
